import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
} from "@chakra-ui/react";
import { groupBy } from "lodash";
import React, { useMemo } from "react";
import { ReportReviewFragment, useGetAnswersHistory } from '../../../graphql/generated/definitions';
import useDateFnsLocale from "../../../hooks/useDateFnsLocale";
import { dateFormatter } from "../../../hooks/useFormatDate";
import useTranslations from "../../../hooks/useTranslations";
import Answer from "./QuestionsImpl/utils/Answer";

const AnswersByReport = ({ answers, reportId, review }) => {
  const t = useTranslations();
  const dateFnsLocale = useDateFnsLocale();

  const collaboratorAnswer = useMemo(
    () =>
      answers[reportId].find((answer) => answer.user.id === review.collaboratorId),
    [answers, reportId, review]
  );

  const referentAnswers = useMemo(
    () =>
      answers[reportId].filter((answer) => answer.user.id !== review.collaboratorId),
    [answers, reportId, review]
  );

  const reportDate = useMemo(
    () => answers[reportId]?.[0]?.report.createdAt || undefined,
    [answers, reportId]
  );

  return (
    <>
      <p className="pb-4 text-sm font-semibold">
        {t("reports.reportFrom", {
          date: dateFormatter(
            reportDate,
            t("dateFormat.short") as string,
            t("dateFormat.fallback") as string,
            dateFnsLocale
          ),
        })}
      </p>
      {collaboratorAnswer && (
        <>
          <p className="text-sm">{t("reports.collaboratorAnswer")}</p>
          <div className="py-2" id={`collaboratorAnswer-${collaboratorAnswer.id}`}>
            <Answer
              answer={collaboratorAnswer}
              withComment
              comment={collaboratorAnswer.value.comment}
              key={collaboratorAnswer.id}
            />
          </div>
        </>
      )}
      {referentAnswers.length > 0 && (
        <>
          <div className="py-2">
            <p className="text-sm">
              {t("reports.evaluatorsAnswers", {
                count: referentAnswers.length,
              })}
            </p>
            {referentAnswers.map((answer) => {
              return (
                <div
                  className="py-2"
                  key={answer.id}
                  id={`referentAnswer-${answer.id}`}
                >
                  <Answer
                    answer={answer}
                    withComment
                    comment={answer.value.comment}
                    key={answer.id}
                  />
                </div>
              );
            })}
          </div>
        </>
      )}
    </>
  );
};

interface AnswerHistoryModalProps {
  open: boolean;
  onClose: VoidFunction;
  questionDigest: string;
  review: ReportReviewFragment;
}

const AnswerHistoryModal: React.FC<AnswerHistoryModalProps> = ({
  open,
  onClose,
  questionDigest,
  review,
}) => {
  const t = useTranslations();

  const { data } = useGetAnswersHistory({
    variables: {
      questionDigest: [questionDigest],
      reviewId: review.id,
      direction: "DESC",
      sortBy: "createdAt",
    },
  });

  const answers = groupBy(data?.answers?.collection, "reportId");

  return (
    <Modal onClose={onClose} isOpen={open} size="xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{t("reports.answerHistory")}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {Object.keys(answers).map((reportId) => {
            return (
              <div className="pb-6" key={reportId}>
                <AnswersByReport
                  answers={answers}
                  reportId={reportId}
                  review={review}
                />
              </div>
            );
          })}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default AnswerHistoryModal;
